import * as T from 'three';
import {PileView} from './pile-view.js';
import {renderQuality} from './render-quality.js';
import {createJellyStudio,disposeJellyStudio} from './jelly-environment.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { SoftBody } from './soft-body.js';
import {AuroraSurface} from './aurora-field.js';
import {MagneticPutty} from './magnetic-putty.js';
import {makeMagnetTool,magnetDemoTarget,createMagnetDemoPath} from './magnet-tool.js';
import { makeMachine, capsule } from './machine.js';
import { dispensePose, revealPose } from './gacha-motion.js';
import {GachaAccent} from './gacha-accent.js';
import { capsuleDisplayPose } from './machine-capsules.js';
import { catalogItem, isTearKind, modelHref, tearProfile } from './catalog.js';
import { toySound } from './toy-sound.js';
import { variantBridgeFromCut } from './variants.js';
import {createBeadGroups,resizeBeadGroups,sortGelBeads} from './bead-rendering.js';
import {StickyPieces} from './sticky-pieces.js';
import {bindStickyVertices} from './sticky-bindings.js';
import {createStickyBridges,updateStickyBridges} from './sticky-bridges.js';
import { BeadCluster } from './bead-cluster.js';
import { CherryPair } from './cherry-pair.js';
import { bindSurface, sampleSurface, prepareSurface, updateSurfaceBounds, splitTriangles } from './surface-deformation.js';
import { stageLayout, playCameraDistance } from './stage-layout.js';
import { PouchSkin, pouchRetention } from './pouch-skin.js';
import { CrackleShell, shellPieceIndex } from './crackle-shell.js';
import { finishToyMaterial } from './toy-materials.js';
import { fitPlayObject } from './play-bounds.js';
import { MeshExtrusion } from './mesh-extrusion.js';
import { TearPair } from './tear-pair.js';
import { ModelCache } from './model-cache.js';
import { waxFragmentPose, waxTremble } from './wax-crumble.js';
import { crackProfile } from './crack-profiles.js';
import { crackMelts } from './crack-profiles.js';
import { createToyExtras } from './toy-extras.js';
import { ToyFace, heartGeometry } from './toy-face.js';
import { createToyBehavior } from './toy-behaviors.js';
import { ToyCarry } from './toy-carry.js';
const tmpContact=new T.Vector3();
// One heart shape and finish, shared by every toy's heart eyes.
const HEART={geometry:null,material:null};
import { createTearBridge, updateTearBridge } from './tear-bridge.js';

const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const ease=x=>1-(1-clamp(x,0,1))**3;
export class Stage {
  constructor(canvas,events){
    this.canvas=canvas;this.events=events;this.state=null;this.phaseStart=0;this.previous=performance.now();
    this.loader=new GLTFLoader();this.cache=new ModelCache(kind=>this.loader.loadAsync(modelHref(kind)),{limit:4});this.sequence=0;this.meshes=[];this.shells=[];this.crackle=null;
    this.renderer=new T.WebGLRenderer({canvas,antialias:true,alpha:true,powerPreference:'high-performance'});
    this.renderer.transmissionResolutionScale=.5;
    this.renderer.shadowMap.enabled=true;this.renderer.shadowMap.type=T.PCFShadowMap;this.renderer.shadowMap.autoUpdate=false;
    this.renderer.toneMapping=T.ACESFilmicToneMapping;this.renderer.toneMappingExposure=.92;
    this.scene=new T.Scene();this.scene.background=new T.Color('#f7f4ef');
    // A small near plane keeps shards and a toy pulled toward the lens from
    // being sliced open. 24-bit depth still resolves a 2500:1 range at play distance.
    this.camera=new T.PerspectiveCamera(38,1,.02,50);
    this.pmrem=new T.PMREMGenerator(this.renderer);const room=new RoomEnvironment();
    this.environment=this.pmrem.fromScene(room,.04);this.scene.environment=this.environment.texture;
    this.scene.environmentIntensity=.55;room.dispose();
    this.hemi=new T.HemisphereLight('#fff8f1','#b4a1c7',1.2);this.scene.add(this.hemi);
    const key=new T.DirectionalLight('#fff9f0',2.6);key.position.set(-3,7,5);key.castShadow=true;
    this.key=key;key.shadow.mapSize.set(512,512);key.shadow.camera.left=-5;key.shadow.camera.right=5;key.shadow.camera.top=5;key.shadow.camera.bottom=-5;
    key.shadow.normalBias=.035;key.shadow.bias=-.0001;key.shadow.radius=5;key.shadow.blurSamples=8;this.scene.add(key);
    const fill=new T.DirectionalLight('#e9ddff',1.2);fill.position.set(4,3,-3);this.scene.add(fill);this.fill=fill;
    const floor=new T.Mesh(new T.PlaneGeometry(200,200),new T.ShadowMaterial({opacity:.13}));floor.rotation.x=-Math.PI/2;floor.receiveShadow=true;floor.position.y=-.05;this.scene.add(floor);this.floor=floor;
    this.platform=new T.Mesh(new T.CylinderGeometry(1.6,1.65,.1,80),new T.MeshPhysicalMaterial({color:'#e8e0ef',roughness:.55,clearcoat:.15}));
    this.platform.position.y=-.015;this.platform.receiveShadow=true;this.scene.add(this.platform);
    const trim=new T.Mesh(new T.TorusGeometry(1.54,.012,8,96),new T.MeshStandardMaterial({color:'#fdf9ff',roughness:.4}));trim.rotation.x=Math.PI/2;trim.position.y=.045;this.scene.add(trim);this.platformTrim=trim;
    this.machine=makeMachine();this.scene.add(this.machine.group);
    this.gachaAccent=new GachaAccent();this.scene.add(this.gachaAccent.group);
    this.pack=capsule(.25,'#c2adde');this.scene.add(this.pack);this.pack.visible=false;
    for(const mesh of this.pack.children){mesh.material=mesh.material.clone();mesh.material.transparent=true;}
    this.toy=new T.Group();this.toy.position.y=.07;this.scene.add(this.toy);
    this.ray=new T.Raycaster();this.pointer=new T.Vector2();this.plane=new T.Plane();this.hit=new T.Vector3();this.active=null;
    this.resize();window.addEventListener('resize',()=>this.handleViewportResize());
    canvas.addEventListener('pointerdown',e=>this.down(e));canvas.addEventListener('pointermove',e=>this.move(e));
    for(const event of ['pointerup','pointercancel','lostpointercapture'])canvas.addEventListener(event,e=>this.up(e));
    canvas.addEventListener('contextmenu',e=>{if(this.state?.screen==='pile')e.preventDefault();});
    canvas.addEventListener('wheel',e=>{if(this.state?.screen==='pile'){this.pileView?.wheel(e);return;}if(this.state?.mode==='orbit'){e.preventDefault();this.zoom=clamp((this.zoom??1)+e.deltaY*.0005,.75,1.35);this.resize();}},{passive:false});
    window.addEventListener('blur',()=>this.release('window-blur'));
    document.addEventListener('visibilitychange',()=>{this.previous=performance.now();if(document.hidden)this.release('hidden');});
    canvas.addEventListener('webglcontextlost',e=>{e.preventDefault();this.contextLost=true;this.release();this.events.status('graphics-error');});
    canvas.addEventListener('webglcontextrestored',()=>{this.contextLost=false;this.dirty=true;this.shadowDirty=true;this.previous=performance.now();this.events.status(null);});
    this.frame=0;this.frameTimes=[];this.loop();
  }
  handleViewportResize(){
    const width=window.innerWidth,height=window.innerHeight,pixelRatio=window.devicePixelRatio;
    const safeBottom=parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom'))||0;
    const previous=this.appliedViewportSize;
    if(previous&&width===previous.width&&height===previous.height&&pixelRatio===previous.pixelRatio&&safeBottom===previous.safeBottom){
      this.pendingViewportResize=false;return;
    }
    // Browser chrome can change a phone's height during a grip. Preserve both
    // the input frame and camera until release; rotation still applies at once.
    const mode=stageLayout(width,height,this.state?.screen,safeBottom).mode;
    if(this.active&&previous&&width===previous.width&&pixelRatio===previous.pixelRatio&&mode===previous.mode){
      this.pendingViewportResize=true;return;
    }
    this.resize();
  }
  flushPendingResize(){
    if(!this.pendingViewportResize||this.active)return;
    this.pendingViewportResize=false;this.resize();
  }
  resize(){
    const w=window.innerWidth,h=window.innerHeight;this.dirty=true;this.shadowDirty=true;
    this.pendingViewportResize=false;
    this.release();
    const safeBottom=parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom'))||0;
    const zoom=this.zoom??1,v=stageLayout(w,h,this.state?.screen,safeBottom);this.viewport=v;
    const previousSimplification=this.simplifyToy;
    this.mobile=v.mode!=='desktop';this.canvas.dataset.layout=v.mode;
    const singleToy=this.state?.screen==='play'||this.state?.screen==='gacha'&&this.state?.phase==='revealed';
    const quality=renderQuality(v,window.devicePixelRatio,{singleToy});
    this.simplifyToy=quality.simplifyToy;
    if(previousSimplification!==undefined&&previousSimplification!==this.simplifyToy&&this.kind)this.refreshToyQuality();
    this.renderer.transmissionResolutionScale=quality.transmissionScale;
    if(this.key)this.key.shadow.normalBias=this.simplifyToy?.015:.008;
    if(this.key&&this.key.shadow.mapSize.x!==quality.shadowSize){
      this.key.shadow.mapSize.setScalar(quality.shadowSize);this.key.shadow.map?.dispose();this.key.shadow.map=null;
    }
    // Allocate pixels only for the playable area, not empty UI margins.
    const ratio=quality.pixelRatio;
    this.renderer.setPixelRatio(ratio);this.renderer.setSize(v.width,v.height,false);
    this.renderer.setViewport(0,0,v.width,v.height);
    Object.assign(this.canvas.style,{left:`${v.left}px`,top:`${v.top}px`,right:'auto',bottom:'auto',width:`${v.width}px`,height:`${v.height}px`});
    this.canvas.dataset.pixelRatio=ratio.toFixed(2);
    this.canvas.dataset.quality=this.simplifyToy?'balanced':'full';
    this.canvas.style.maskImage=v.mode==='mobile'?`linear-gradient(to bottom,transparent,#000 20px,#000 calc(100% - 20px),transparent)`:'none';
    this.camera.aspect=v.width/v.height;this.camera.fov=38;
    const machineView=this.state?.screen==='gacha'&&this.state?.phase!=='revealed';
    const distance=machineView?Math.max(6.8,3.5/(2*Math.tan(19*Math.PI/180)*this.camera.aspect)):playCameraDistance(v);
    this.homeTarget=new T.Vector3(0,machineView?1.65:1.17,0);
    this.homePosition=this.homeTarget.clone().add(new T.Vector3(.55,1.1,distance).multiplyScalar(zoom));
    this.camera.position.copy(this.homePosition);this.camera.lookAt(this.homeTarget);
    this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
    this.pileView?.resize(v);
    this.appliedViewportSize={width:w,height:h,pixelRatio:window.devicePixelRatio,safeBottom,mode:v.mode};
  }
  refreshToyQuality(){
    // Screen workload chooses quality independently of the responsive layout.
    // Retain the mesh, deformation and physics when changing material detail.
    if(this.kind==='crunchball')for(const item of this.meshes){
      if(item.sourceMaterial?.name!=='beads_clear_skin')continue;
      const previous=item.mesh.material;
      item.mesh.material=finishToyMaterial(item.sourceMaterial.clone(),this.jellyEnvironment?.texture,item.mesh.geometry.boundingBox,{kind:this.kind,mobile:this.simplifyToy,variant:this.variant});
      item.mesh.material.needsUpdate=true;previous.dispose();
    }
    resizeBeadGroups(this.beadRenderGroups??[],this.kind,this.simplifyToy);
  }
  sync(state){
    this.dirty=true;
    const phaseChanged=state.phase!==this.lastPhase;
    if(phaseChanged){this.phaseStart=performance.now()/1000;this.lastPhase=state.phase;this.sealSent=false;this.revealSettled=false;}
    const screenChanged=this.lastScreen!==state.screen;
    if(screenChanged&&this.state?.screen==='gacha'&&this.state.phase==='revealed'&&!this.revealSettled)this.toy.rotation.z=0;
    this.lastScreen=state.screen;
    this.state=state;if(screenChanged||phaseChanged){this.release();this.resize();}this.canvas.dataset.phase=state.phase;this.canvas.dataset.screen=state.screen;
    if(state.mode!==this.lastMode){this.release();this.lastMode=state.mode;}
    if(state.screen==='pile'){
      this.pileView??=new PileView(this.environment.texture);this.pileView.resize(this.viewport);this.pileView.setMode?.(state.pileMode??'touch');
      const key=JSON.stringify([state.pileIds,state.pileRevision??0,state.reducedMotion]);
      if(key!==this.pileKey){
        this.pileKey=key;const request=this.pileRequest={};this.events.status('loading');
        const toys=(state.pileIds??[]).map(id=>state.collection.find(toy=>toy.id===id)).filter(Boolean);
        this.pileView.load(toys,state.reducedMotion).then(loaded=>{if(loaded&&this.state.screen==='pile'&&this.pileRequest===request)this.events.status(null);}).catch(error=>{console.error('Pile loading failed',error);if(this.state.screen==='pile'&&this.pileRequest===request)this.events.status('model-error');});
      }
      return;
    }
    if(this.pileKey){this.pileView.clear();this.pileKey=null;this.pileRequest=null;this.events.status(this.loading?'loading':null);}
    const record=state.previewKind?{id:`preview:${state.previewKind}:${state.previewRevision}`,kind:state.previewKind,waxBroken:state.previewWaxBroken,waxDamage:state.previewWaxDamage}:state.collection.find(t=>t.id===state.selectedId);
    if(record && record.id!==this.recordId){this.recordId=record.id;this.loadToy(record);}
    if(state.pending)this.prefetch(state.pending.kind).catch(error=>{console.error('Model preload failed',error);});
    this.canvas.style.cursor='default';
  }
  prefetch(kind){
    this.cache.retain([this.kind,this.loadingKind,this.state?.pending?.kind,kind]);
    return this.cache.get(kind);
  }
  async loadToy(record){
    const sequence=++this.sequence;this.loadingKind=record.kind;this.loading=true;this.shadowDirty=true;this.release();this.events.status('loading');
    try {
      const gltf=await this.prefetch(record.kind);if(sequence!==this.sequence)return;
      if(this.state.screen!=='pile')this.release();
      for(const item of this.meshes){item.mesh.geometry.dispose();item.mesh.material.dispose();}
      for(const group of this.beadRenderGroups??[]){group.dispose();group.geometry.dispose();group.material.dispose();}
      this.meshBulbs?.dispose();this.meshBulbs?.geometry.dispose();this.meshBulbs?.material.dispose();this.meshBulbs=null;
      this.tearBridge?.geometry.dispose();this.tearBridge?.material.dispose();this.tearBridge=null;
      this.stickyBridges?.geometry.dispose();this.stickyBridges?.material.dispose();this.stickyBridges=null;
      this.extras?.dispose();this.extras=null;this.face=null;this.faceItems=[];this.behavior?.dispose();this.behavior=null;this.magnetTool?.userData.dispose();this.magnetTool=null;this.magnet=null;this.toy.clear();this.meshes=[];this.shells=[];this.shellOrigin=null;this.aurora=null;
      this.toy.rotation.set(0,-.18,0);this.toy.scale.setScalar(1);
      this.playFitScale=1;this.playFitSettling=false;
      this.carry??=new ToyCarry();this.carry.reset();this.carryOn=false;
      if(['amberjelly','waterdrop'].includes(record.kind)&&!this.jellyEnvironment&&this.pmrem){
        const studio=createJellyStudio();this.jellyEnvironment=this.pmrem.fromScene(studio,.015);disposeJellyStudio(studio);
      }
      const source=gltf.scene.clone(true);source.updateMatrixWorld(true);
      const initialBox=new T.Box3().setFromObject(source),size=initialBox.getSize(new T.Vector3()),center=initialBox.getCenter(new T.Vector3());
      const scale=2.25/Math.max(size.y,size.x*.91);
      const transform=new T.Matrix4().makeScale(scale,scale,scale).multiply(new T.Matrix4().makeTranslation(-center.x,-initialBox.min.y,-center.z));
      // Where the faces sit, to find the coarse skins under them.
      const faceCentres=[];
      source.traverse(node=>{if(node.isMesh&&/^face_/.test(node.name)){node.geometry.computeBoundingBox();faceCentres.push(node.geometry.boundingBox.getCenter(new T.Vector3()).applyMatrix4(transform.clone().multiply(node.matrixWorld)));}});
      source.traverse(node=>{
        if(!node.isMesh)return;
        const geo=node.geometry.clone();geo.applyMatrix4(transform.clone().multiply(node.matrixWorld));
        // A pouch's film matches its filling vertex for vertex, so it keeps its mesh.
        if(faceCentres.length&&geo.index&&geo.attributes.position.count<3000&&catalogItem(record.kind)?.material!=='pouch'&&!/^(face_|prop_|shell_|bead_|film_|seam_)/.test(node.name)){
          geo.computeBoundingBox();const reach=geo.boundingBox.clone().expandByScalar(.08);
          if(faceCentres.some(c=>reach.containsPoint(c)))splitTriangles(geo);
        }
        geo.computeBoundingBox();
        const mat=finishToyMaterial(node.material.clone(),this.jellyEnvironment?.texture,geo.boundingBox,{kind:record.kind,mobile:this.simplifyToy,variant:record.variant});
        const isShell=node.name.startsWith('shell_');
        if(mat.isMeshStandardMaterial && !mat.isMeshPhysicalMaterial){ /* Exported PBR colors remain authored. */ }
        // Painted-on faces sit a hair above the skin. A deep dent closes that gap,
        // so the face wins the depth test where they meet instead of sinking in.
        if(/^face_/.test(node.name))Object.assign(mat,{polygonOffset:true,polygonOffsetFactor:-3,polygonOffsetUnits:-6});
        const mesh=new T.Mesh(geo,mat);mesh.name=node.name;mesh.castShadow=!(mat.transparent&&mat.opacity<.3)&&mat.name!=='beads_clear_skin';mesh.receiveShadow=mesh.castShadow;
        if(mat.userData.updateVolume)mesh.onBeforeRender=()=>mat.userData.updateVolume(mesh.matrixWorld,geo.boundingBox);
        mesh.frustumCulled=false;this.toy.add(mesh);const rest=geo.attributes.position.array.slice();
        geo.computeBoundingBox();
        const item={mesh,sourceMaterial:node.material,rest,isFilm:/^(film_|seam_)/.test(node.name),isProp:/^prop_/.test(node.name),restMinY:geo.boundingBox.min.y,bindings:[],isShell,center:geo.boundingBox.getCenter(new T.Vector3()),rigidParticle:/^bead_/.test(node.name)};this.meshes.push(item);
        if(record.kind==='cherry'){
          item.cherrySide=[-1,0,1].includes(node.userData.cherry_side)?node.userData.cherry_side:/^(leaf|stem_joint)/.test(node.name)?0:item.center.x<0?-1:1;
          if(node.name.startsWith('stem')&&item.cherrySide!==0){
            const root=node.userData.cherry_root?new T.Vector3().fromArray(node.userData.cherry_root).applyMatrix4(transform).y:geo.boundingBox.min.y;
            const joint=node.userData.cherry_joint?new T.Vector3().fromArray(node.userData.cherry_joint).applyMatrix4(transform).y:geo.boundingBox.max.y;
            item.cherryJointWeights=new Float32Array(rest.length/3);
            for(let i=0;i<item.cherryJointWeights.length;i++){
              const t=clamp((rest[i*3+1]-root)/Math.max(.001,joint-root),0,1);
              item.cherryJointWeights[i]=t*t*(3-2*t);
            }
          }
        }
        if(isShell){
          item.shellIndex=shellPieceIndex(node.name);item.restNormals=geo.attributes.normal.array.slice();
          const group=this.shells[item.shellIndex]??={bounds:new T.Box3(),center:new T.Vector3(),restMinY:Infinity};
          group.bounds.union(geo.boundingBox);group.bounds.getCenter(group.center);group.restMinY=group.bounds.min.y;
        }
      });
      const box=new T.Box3().setFromObject(this.toy);this.toy.updateMatrixWorld(true);
      // Bounds come from baked local geometry, independent of the display rotation.
      const isPouch=catalogItem(record.kind)?.material==='pouch',local=new T.Box3(),filmBox=new T.Box3();
      for(const item of this.meshes){
        item.mesh.geometry.computeBoundingBox();
        if(!item.isProp&&(!isPouch||!item.isFilm))local.union(item.mesh.geometry.boundingBox);
        if(item.isFilm)filmBox.union(item.mesh.geometry.boundingBox);
      }
      local.min.addScalar(-.012);local.max.addScalar(.012);
      // The body rests on a floor at its own bottom. A pouch hangs in its film,
      // and the pizza's cheese strings down past its tip, so those two have none.
      this.body=new SoftBody(local.min.toArray(),local.max.toArray(),catalogItem(record.kind)?.material==='wax'?'mochi':record.kind==='pouchmochi'?'pouchwater':catalogItem(record.kind)?.physics??catalogItem(record.kind)?.material,{anchorBottom:false,floor:isPouch||record.kind==='cheesepizza'?null:local.min.y,floorFriction:.8});
      if(this.body.floor!=null){
        // The skin's underside, where it rests on the floor.
        const low=local.min.y+.012,height=local.max.y-local.min.y,feet=[];
        for(const item of this.meshes)if(!item.isProp&&!item.isShell&&!item.isFilm&&!/^face_/.test(item.mesh.name))for(let i=0;i<item.rest.length;i+=3)if(item.rest[i+1]<low+height*.03)feet.push([item.rest[i],item.rest[i+1],item.rest[i+2]]);
        this.body.feet=feet.filter((_,i)=>i%Math.max(1,Math.ceil(feet.length/24))===0);
      }
      if(record.kind==='magnetputty'){
        this.magnet=new MagneticPutty(this.body,{surfacePoints:this.meshes.find(item=>item.mesh.name==='body_magnetputty')?.rest});
        this.magnetTool=makeMagnetTool(Math.max(...local.max.toArray().map((v,a)=>v-local.min.getComponent(a))));
        this.magnetTool.position.fromArray(this.magnet.position);this.toy.add(this.magnetTool);
      }
      if(record.kind==='auroragel')this.aurora=new AuroraSurface(local.min.toArray(),local.max.toArray(),this.meshes.map(item=>item.mesh));
      for(const group of this.shells)group.binding=this.body.bind(group.center.toArray());
      this.kind=record.kind;this.variant=record.variant;this.canvas.dataset.kind=record.kind;
      this.stickyPieces=null;
      if(record.kind==='warabimochi'){
        const pieces=this.meshes.filter(item=>item.mesh.name.startsWith('body_slime_warabi'));
        const centers=pieces.map(item=>item.center.toArray()),sizes=pieces.map(item=>item.mesh.geometry.boundingBox.getSize(new T.Vector3()).toArray());
        this.stickyPieces=new StickyPieces({centers,sizes,reach:1.8});
        for(const item of this.meshes){
          item.pieceIndex=pieces.indexOf(item);
          item.stickyIndices=item.pieceIndex>=0?new Uint8Array(item.rest.length/3).fill(item.pieceIndex):bindStickyVertices(item.mesh.geometry,centers);
          item.restNormals=item.mesh.geometry.attributes.normal.array.slice();
        }
        const material=pieces[0].mesh.material.clone();material.vertexColors=false;
        // Match the undusted lower half; the neck is exposed moist mochi.
        const colors=pieces[0].mesh.geometry.attributes.color,vertices=pieces[0].mesh.geometry.attributes.position;
        if(colors){
          const color=new T.Color(0,0,0);let count=0;
          for(let i=0;i<colors.count;i++)if(vertices.getY(i)<centers[0][1]){color.r+=colors.getX(i);color.g+=colors.getY(i);color.b+=colors.getZ(i);count++;}
          if(count)material.color.copy(color.multiplyScalar(1/count));
        }
        this.stickyBridges=new T.Mesh(createStickyBridges(),material);
        this.stickyBridges.name='warabi_sticky_necks';this.stickyBridges.castShadow=true;this.stickyBridges.visible=false;this.toy.add(this.stickyBridges);
      }
      this.cherries=record.kind==='cherry'?new CherryPair():null;
      this.tearPair=isTearKind(record.kind)?new TearPair({anchors:[-.01,.01].map(x=>new T.Vector3(x,.64,0).applyMatrix4(transform).toArray()),scale,...tearProfile(record.kind,record.variant)}):null;
      if(this.tearPair){
        this.tearAnchorBindings=this.tearPair.anchors.map(p=>this.body.bind(p));
        // A cut face whose material name contains '_bridge' (cheese, marshmallow)
        // also supplies the strands; otherwise they take the outer skin's material.
        const bridgeSource=(variantBridgeFromCut(record.kind,record.variant)?this.meshes.find(item=>item.mesh.material.name.includes('_cut')):null)??this.meshes.find(item=>item.mesh.material.name.includes('_bridge'))??this.meshes[0];
        this.tearBridge=new T.Mesh(createTearBridge(),bridgeSource.mesh.material.clone());
        this.tearBridge.castShadow=true;this.toy.add(this.tearBridge);
      }
      this.meshExtrusion=record.kind==='meshball'?new MeshExtrusion({center:new T.Vector3(0,.9,0).applyMatrix4(transform).toArray(),scale}):null;
      if(this.meshExtrusion){
        const material=this.meshes.find(item=>item.mesh.name==='body_netgel').mesh.material.clone();
        this.meshBulbs=new T.InstancedMesh(new T.SphereGeometry(1,16,12),material,this.meshExtrusion.getBulbs().length);
        this.meshBulbs.frustumCulled=false;this.meshBulbs.castShadow=true;this.toy.add(this.meshBulbs);
        this.meshBulbBindings=this.meshExtrusion.getBulbs().map(p=>this.body.bind(p.direction.map((n,a)=>this.meshExtrusion.center[a]+n*.735*scale)));
      }
      const filmBounds={min:filmBox.min.toArray(),max:filmBox.max.toArray()};
      this.pouch=isPouch?new PouchSkin(this.body,filmBounds):null;
      for(const item of this.meshes){
        if(this.meshExtrusion&&item.mesh.name==='body_netgel'){
          item.extrusionBindings=[];
          for(let i=0;i<item.rest.length;i+=3)item.extrusionBindings.push(this.meshExtrusion.bind([item.rest[i],item.rest[i+1],item.rest[i+2]]));
        }
        item.centerBinding=this.body.bind(item.center.toArray());
        item.transportNormals=!item.rigidParticle&&record.kind!=='meshball'&&item.mesh.name!=='body_beads'&&!/^(stem|leaf)/.test(item.mesh.name);
        const normals=item.mesh.geometry.attributes.normal;
        if(item.rigidParticle||item.isShell||item.isProp||this.stickyPieces)continue;
        for(let i=0;i<item.rest.length;i+=3){
          const point=[item.rest[i],item.rest[i+1],item.rest[i+2]];
          const binding=item.transportNormals?bindSurface(this.body,point,[normals.array[i],normals.array[i+1],normals.array[i+2]]):this.body.bind(point);
          if(item.isFilm&&this.pouch)binding.retention=pouchRetention(point,this.body,filmBounds);
          item.bindings.push(binding);
        }
      }
      this.particles=this.meshes.filter(item=>item.rigidParticle);
      this.beads=this.particles.length?new BeadCluster(this.particles.map(item=>item.center.toArray()),this.particles.map(item=>item.mesh.geometry.boundingBox.getSize(new T.Vector3()).length()/Math.sqrt(3)/2)):null;
      this.particles.forEach((item,index)=>item.particleIndex=index);
      this.beadRenderGroups=[];
      if(record.kind==='crunchball'||record.kind==='gelbeads'){
        this.beadRenderGroups=createBeadGroups(this.particles,record.kind,this.simplifyToy);
        for(const group of this.beadRenderGroups)this.toy.add(group);
      }
      for(const item of this.meshes)if(item.mesh.name==='body_beads'){
        item.nearestParticles=[];
        for(let i=0;i<item.rest.length;i+=3){
          let nearest=0,distance=Infinity;
          for(let j=0;j<this.particles.length;j++){
            const c=this.particles[j].center,d=Math.hypot(item.rest[i]-c.x,item.rest[i+1]-c.y,item.rest[i+2]-c.z);
            if(d<distance){distance=d;nearest=j;}
          }
          item.nearestParticles.push(nearest);
        }
      }
      this.crackle=this.shells.length?new CrackleShell(this.shells.map(s=>s.center.toArray()),record.waxBroken?this.shells.map((_,i)=>i):record.waxDamage??[],crackProfile(record.kind)):null;this.pressTime=0;this.needsSurfaceUpdate=true;
      this.melt=0;
      for(const item of this.meshes)if(crackMelts(record.kind)&&!item.isShell){item.melts=true;item.transportNormals=false;}
      this.extras=createToyExtras(this,record);
      // Toys with eyes blink now and then, and shut them tight while pressed.
      this.faceItems=this.meshes.filter(item=>/^face_(eyes?|glints?)(\.?\d+)?$/.test(item.mesh.name)&&!item.isShell);
      const middle=this.body?new T.Vector3(...this.body.min.map((n,a)=>(n+this.body.max[a])/2)):null;
      this.face=this.faceItems.length?new ToyFace(this.faceItems.map(item=>({name:item.mesh.name,rest:item.rest})),{reducedMotion:!!this.state?.reducedMotion,center:middle}):null;
      if(!this.face?.parts)this.face=null;this.facePosed=false;this.facePose=false;
      this.behavior=createToyBehavior(this,record);if(this.behavior?.rotation)this.toy.rotation.set(...this.behavior.rotation);
      // Heart eyes, one per eye, beside it (a behaviour may have moved the eye into a group).
      // They cover the shut eye, so they win the depth test over the face parts.
      // Scaled to nothing while unused, so they never widen the play fit.
      this.faceHearts=(this.face?.parts.eyes??[]).map(eye=>{
        const heart=new T.Mesh(HEART.geometry??=heartGeometry(),HEART.material??=new T.MeshPhysicalMaterial({color:'#f2668b',roughness:.3,clearcoat:1,clearcoatRoughness:.12,polygonOffset:true,polygonOffsetFactor:-8,polygonOffsetUnits:-16}));
        heart.name='heart_eye';heart.matrixAutoUpdate=false;heart.matrix.makeScale(1e-6,1e-6,1e-6);heart.visible=false;
        (this.faceItems[eye.index].mesh.parent??this.toy).add(heart);return heart;
      });
      const root=typeof document!=='undefined'?document.documentElement:null;if(root?.dataset)root.dataset.carry=this.carryable()?'1':'0';
      toySound.use(record.kind);this.soundDrag=null;
      await this.renderer.compileAsync(this.toy,this.camera,this.scene);
      if(sequence!==this.sequence)return;
      this.loading=false;this.loadingKind=null;this.cache.retain([this.kind,this.state?.pending?.kind]);this.canvas.dataset.cachedModels=String(this.cache.completedCount);this.dirty=true;this.shadowDirty=true;if(this.state.screen!=='pile')this.events.status(null);
    } catch(error){console.error('Model loading failed',error);if(sequence===this.sequence){this.loading=false;this.recordId=null;if(this.state.screen!=='pile')this.events.status('model-error');}}
  }
  setRay(e){const r=this.viewport;this.pointer.set((e.clientX-r.left)/r.width*2-1,-(e.clientY-r.top)/r.height*2+1);this.ray.setFromCamera(this.pointer,this.camera);}
  pickableMeshes(){const meshes=this.meshes.filter(m=>!m.instances&&!m.isProp&&!m.unpickable&&(!m.isShell||!this.crackle?.isBroken(m.shellIndex))&&(!this.pouch||this.state?.mode==='orbit'||!m.isFilm)).map(m=>m.mesh);if(this.meshBulbs)meshes.push(this.meshBulbs);if(this.magnetTool)meshes.push(...this.magnetTool.children);if(this.behavior?.pickable)meshes.push(...this.behavior.pickable());return meshes;}
  // Squash each eye about where it is now; its glints go with it and hide while it is shut.
  poseFace(){
    const f=this.face,poses=[];
    f.parts.eyes.forEach((eye,i)=>{
      const mesh=this.faceItems[eye.index].mesh,box=mesh.geometry.boundingBox??(mesh.geometry.computeBoundingBox(),mesh.geometry.boundingBox),center=box.getCenter(new T.Vector3());
      poses[i]=f.matrix(i,center).clone();
      mesh.matrixAutoUpdate=false;mesh.matrix.copy(poses[i]);mesh.matrixWorldNeedsUpdate=true;
      const heart=this.faceHearts?.[i];
      if(heart){heart.visible=f.heart>.01&&mesh.visible;heart.matrix.copy(heart.visible?f.heartMatrix(i,center):heart.matrix.makeScale(1e-6,1e-6,1e-6));heart.matrixWorldNeedsUpdate=true;}
    });
    for(const glint of f.parts.glints){
      const mesh=this.faceItems[glint.index].mesh;
      mesh.matrixAutoUpdate=false;mesh.matrix.copy(poses[glint.eye]);mesh.matrixWorldNeedsUpdate=true;
      // A toy can hide its eyes (asleep); glints stay hidden with them.
      mesh.visible=f.glintVisible&&this.faceItems[f.parts.eyes[glint.eye].index].mesh.visible;
    }
  }
  // Lids move before physics; the pose waits for it (followFace), so a shut eye
  // squashes about where the skin has taken it this frame and stays on the skin.
  stepFace(dt,reduced){
    if(!this.face||!this.canTouch())return;
    this.face.reducedMotion=reduced;const moving=this.face.step(dt,!!this.body?.hold);
    this.facePose=moving||this.facePosed||Math.abs(this.face.openness-1)>.002||this.face.heart>.001;
    if(moving||this.facePosed)this.dirty=true;this.facePosed=moving;
  }
  followFace(){if(this.facePose){this.poseFace();this.facePose=false;}}
  canTouch(){return !this.contextLost&&!this.loading&&!!this.recordId&&(this.state?.screen==='play' || this.state?.phase==='revealed'&&this.state.screen==='gacha');}
  down(e){
    if(this.state?.screen==='pile'){this.pileView?.down(e,this.canvas);return;}
    if(e.button!==0)return;
    if(this.active){this.addGrip(e);return;}
    if(this.state?.screen==='gacha'&&this.state.phase==='idle'){this.setRay(e);if(this.ray.intersectObject(this.machine.knob,true).length)this.events.draw();return;}
    if(this.state?.phase==='sealed'&&this.state.screen==='gacha'){
      this.setRay(e);if(this.ray.intersectObject(this.pack,true).length)this.events.open();return;
    }
    if(!this.canTouch()||!this.body)return;
    this.setRay(e);this.behavior?.nudge?.(this.ray.ray);
    const hits=this.ray.intersectObjects(this.pickableMeshes(),false);
    if(!hits.length)return;
    // A real hit takes ownership from the demonstration, including its support.
    if(this.autoPressUntil||this.autoTearUntil||this.autoMagnetUntil||this.magnet?.active)this.release('manual-interaction');
    e.preventDefault();this.dirty=true;this.canvas.setPointerCapture(e.pointerId);
    this.active={id:e.pointerId,x:e.clientX,y:e.clientY,at:performance.now(),pointerType:e.pointerType,orbit:this.state.mode==='orbit'};
    this.dragBias=[0,0,0];this.gripCorrection=null;
    this.events?.touch?.(this.kind);
    this.canvas.dataset.grabs=String(Number(this.canvas.dataset.grabs??0)+1);
    if(this.active.orbit)return;
    toySound.press();this.soundDrag=null;
    const hit=hits[0].point;
    this.plane.setFromNormalAndCoplanarPoint(this.camera.getWorldDirection(new T.Vector3()),hit);
    const local=this.toy.worldToLocal(hit.clone());this.grabPoint=this.pouch?this.pouch.toBody(local.toArray()):local.toArray();
    // Automatic viewport fitting and film flow must not become finger motion.
    // Freeze both coordinate frames and the inverse pouch tangent at contact.
    this.dragFrame={inverse:this.toy.matrixWorld.clone().invert(),origin:local.clone(),camera:this.camera.clone(),pouchTangent:null};
    if(hits[0].object.userData.magnetTool&&this.magnet){
      this.active.magnet=true;this.grabPoint=[...this.magnet.position];this.dragTarget=[...this.grabPoint];
      // Use an immutable skin for approach picking. It is CPU-only and shares
      // the rest/index arrays, so fitting and deformation cannot move its depth.
      const surface=this.meshes.find(item=>item.mesh.name==='body_magnetputty');
      if(surface){
        const geometry=new T.BufferGeometry();geometry.setAttribute('position',new T.BufferAttribute(surface.rest,3));geometry.setIndex(surface.mesh.geometry.index);
        const proxy=new T.Mesh(geometry,surface.mesh.material);proxy.matrixAutoUpdate=false;proxy.matrixWorldAutoUpdate=false;proxy.matrixWorld.copy(surface.mesh.matrixWorld);
        this.dragFrame.magnetSurface=proxy;
      }
      this.magnet.setActive(true);this.magnet.move(this.dragTarget);this.canvas.style.cursor='grabbing';return;
    }
    if(this.behavior?.grab?.(hits[0])==='tool'){
      // A tool slides in its own toy-local plane, so it stays under the finger.
      if(this.behavior.toolNormal){
        const normal=new T.Vector3(...this.behavior.toolNormal).transformDirection(this.toy.matrixWorld);
        this.plane.setFromNormalAndCoplanarPoint(normal,new T.Vector3(...this.grabPoint).applyMatrix4(this.toy.matrixWorld));
        if(this.ray.ray.intersectPlane(this.plane,this.hit))this.dragFrame.origin.copy(this.hit).applyMatrix4(this.dragFrame.inverse);
      }
      this.active.tool=true;this.dragTarget=[...this.grabPoint];this.canvas.style.cursor='grabbing';return;
    }
    // A toy that moves (or is drawn scaled) as a whole gives the matrix from its
    // soft-body space to toy space, so the press lands where the finger is.
    const bodyFrame=this.behavior?.bodyMatrix?.clone().invert();
    if(bodyFrame){local.applyMatrix4(bodyFrame);this.grabPoint=local.toArray();this.dragFrame.inverse.premultiply(bodyFrame);this.dragFrame.origin.copy(local);}
    if(this.pouch){
      const epsilon=Math.max(...this.body.max.map((n,a)=>n-this.body.min[a]))*1e-4;
      const columns=[0,1,2].map(axis=>{
        const plus=local.toArray(),minus=local.toArray();plus[axis]+=epsilon;minus[axis]-=epsilon;
        const p=this.pouch.toBody(plus),m=this.pouch.toBody(minus);return p.map((n,a)=>(n-m[a])/(2*epsilon));
      });
      this.dragFrame.pouchTangent=new T.Matrix3().set(columns[0][0],columns[1][0],columns[2][0],columns[0][1],columns[1][1],columns[2][1],columns[0][2],columns[1][2],columns[2][2]);
      this.grabPoint=this.grabPoint.map((n,a)=>clamp(n,this.body.min[a],this.body.max[a]));
    }
    this.cherries?.grab(local.x<0?-1:1);
    if(this.stickyPieces){
      const item=this.meshes.find(item=>item.mesh===hits[0].object);
      const index=item.pieceIndex>=0?item.pieceIndex:item.stickyIndices[hits[0].face.a];
      this.stickyPieces.grab(index);
    }
    if(this.tearPair){
      const side=hits[0].object.name.startsWith('body_left')?-1:1;
      this.tearPair.grab(side);this.tearPointerOrigin=local.toArray();this.tearStartOffset=this.tearPair.offset(side);
      this.grabPoint=this.grabPoint.map((n,a)=>n-this.tearStartOffset[a]);
    }
    this.body.grab(this.grabPoint);
    const press=(hits[0].normal??hits[0].face.normal).clone().transformDirection(hits[0].object.matrixWorld).negate().transformDirection(this.toy.matrixWorld.clone().invert());
    this.pressDirection=(bodyFrame?press.transformDirection(bodyFrame):press).toArray();
    this.supportPress();
    this.dragTarget=[...this.grabPoint];this.pressTime=0;this.pressStarted=performance.now();this.canvas.style.cursor='grabbing';
    // A pull past the stretch takes the whole toy along; a toy in the air is caught.
    this.carryOn=this.canCarry();this.carryLocal=[0,0,0];if(this.carryOn)this.carry.grab();
  }
  // Toys whose drag already means something else (tearing, peeling pieces,
  // magnets, pouches, cracking shells, stretchy slime) stay where they are.
  canCarry(){
    if(!this.carry||this.state?.screen!=='play'||this.state?.mode==='orbit'||!this.carryable())return false;
    return !this.crackle||this.crackle.damage.length>=this.shells.length;
  }
  carryable(){
    if(this.tearPair||this.cherries||this.stickyPieces||this.magnet||this.pouch||this.shells.length)return false;
    const item=catalogItem(this.kind);if(['slime','magnetic'].includes(item?.physics??item?.material))return false;
    return !this.behavior||!!this.behavior.carry;
  }
  // Pulled further than half its size, the excess moves the toy (across the
  // floor and up, never into it); the skin keeps only the rest of the pull.
  followCarry(dt){
    const shift=this.carryLocal,size=Math.max(...this.body.max.map((n,a)=>n-this.body.min[a]));
    const raw=this.dragTarget.map((n,a)=>n-this.grabPoint[a]+shift[a]),length=Math.hypot(...raw),limit=size*.5;
    const excess=length>limit?raw.map(n=>n*(1-limit/length)):[0,0,0];
    const scale=this.toy.scale.x||1,world=new T.Vector3(...excess).applyQuaternion(this.toy.quaternion).multiplyScalar(scale);
    this.carry.follow([world.x,world.y,0],dt);
    const applied=new T.Vector3(...this.carry.p.map((n,a)=>n-this.carry.start[a])).divideScalar(scale).applyQuaternion(this.toy.quaternion.clone().invert()).toArray();
    const delta=applied.map((n,a)=>n-shift[a]);this.carryLocal=applied;
    this.dragTarget=this.dragTarget.map((n,a)=>n-delta[a]);this.dragBias=this.dragBias.map((n,a)=>n-delta[a]);
  }
  // Hitting the edge of the play space squashes the side that meets it: the far side keeps going.
  bumpCarry({speed,dir}){
    const b=this.body,d=new T.Vector3(...dir).applyQuaternion(this.toy.quaternion.clone().invert()).toArray(),r=b.initialRest,k=Math.min(speed,7)/(this.toy.scale.x||1);
    let lo=Infinity,hi=-Infinity;for(let i=0;i<b.count;i++){const s=r[i*3]*d[0]+r[i*3+1]*d[1]+r[i*3+2]*d[2];lo=Math.min(lo,s);hi=Math.max(hi,s);}
    for(let i=0;i<b.count;i++){const t=(hi-(r[i*3]*d[0]+r[i*3+1]*d[1]+r[i*3+2]*d[2]))/Math.max(1e-6,hi-lo);for(let a=0;a<3;a++)b.velocity[i*3+a]+=d[a]*k*.9*t;}
    b.activity=1;this.face?.flinch?.(.2);this.canvas.dataset.carryBumps=String(Number(this.canvas.dataset.carryBumps??0)+1);
  }
  // Landing squashes the skin: the top keeps falling and the bottom spreads.
  landCarry(impact){
    const b=this.body,height=b.max[1]-b.min[1],cx=(b.min[0]+b.max[0])/2,cz=(b.min[2]+b.max[2])/2,r=b.initialRest,k=Math.min(impact,7)/(this.toy.scale.x||1);
    for(let i=0;i<b.count;i++){
      const t=(r[i*3+1]-b.min[1])/height;
      b.velocity[i*3+1]-=k*1.3*t;b.velocity[i*3]+=(r[i*3]-cx)*k*2.2*(1-t);b.velocity[i*3+2]+=(r[i*3+2]-cz)*k*2.2*(1-t);
    }
    b.activity=1;this.face?.flinch?.(.25);this.canvas.dataset.carryLandings=String(Number(this.canvas.dataset.carryLandings??0)+1);
  }
  addGrip(e){
    // Split toys have their own piece controllers. A second cage constraint
    // would fight those controllers rather than hold the visible piece.
    if(e.pointerType!=='touch'||this.active.pointerType!=='touch'||this.active.orbit||this.active.magnet||this.active.tool||this.secondary||e.pointerId===this.active.id||this.tearPair||this.cherries||this.stickyPieces||!this.canTouch())return;
    this.setRay(e);const hit=this.ray.intersectObjects(this.pickableMeshes(),false).find(hit=>!hit.object.userData.magnetTool&&!hit.object.userData.knifeTool);
    if(!hit)return;
    const bodyFrame=this.behavior?.bodyMatrix?.clone().invert();
    const local=this.toy.worldToLocal(hit.point.clone());if(bodyFrame)local.applyMatrix4(bodyFrame);
    const grabPoint=this.pouch?this.pouch.toBody(local.toArray()).map((n,a)=>clamp(n,this.body.min[a],this.body.max[a])):local.toArray();
    const extent=Math.max(...this.body.max.map((n,a)=>n-this.body.min[a]));
    if(Math.hypot(...grabPoint.map((n,a)=>n-this.grabPoint[a]))<extent*.16)return;
    const inverse=this.toy.matrixWorld.clone().invert();if(bodyFrame)inverse.premultiply(bodyFrame);
    const dragFrame={inverse,origin:local.clone(),camera:this.camera.clone(),pouchTangent:null};
    if(this.pouch){
      const epsilon=extent*1e-4,columns=[0,1,2].map(axis=>{
        const plus=local.toArray(),minus=local.toArray();plus[axis]+=epsilon;minus[axis]-=epsilon;
        const p=this.pouch.toBody(plus),m=this.pouch.toBody(minus);return p.map((n,a)=>(n-m[a])/(2*epsilon));
      });
      dragFrame.pouchTangent=new T.Matrix3().set(columns[0][0],columns[1][0],columns[2][0],columns[0][1],columns[1][1],columns[2][1],columns[0][2],columns[1][2],columns[2][2]);
    }
    const at=performance.now();
    this.secondary={id:e.pointerId,x:e.clientX,y:e.clientY,at,pointerType:'touch',grabPoint,dragTarget:[...grabPoint],dragBias:[0,0,0],dragFrame,pressStarted:at,
      plane:new T.Plane().setFromNormalAndCoplanarPoint(this.camera.getWorldDirection(new T.Vector3()),hit.point),
      pressDirection:(hit.normal??hit.face.normal).clone().transformDirection(hit.object.matrixWorld).negate().transformDirection(inverse).toArray()};
    this.gripCorrection=null;this.supportStart=null;this.body.support(grabPoint);this.dirty=true;
    e.preventDefault();this.canvas.setPointerCapture(e.pointerId);
  }
  moveGrip(e){
    const contact=this.secondary,v=this.viewport,pad=24;
    this.setRay({clientX:clamp(e.clientX,Math.min(v.left+pad,contact.x),Math.max(v.left+v.width-pad,contact.x)),clientY:clamp(e.clientY,Math.min(v.top+pad,contact.y),Math.max(v.top+v.height-pad,contact.y))});
    this.ray.setFromCamera(this.pointer,contact.dragFrame.camera);
    if(!this.ray.ray.intersectPlane(contact.plane,this.hit))return;
    const delta=this.hit.clone().applyMatrix4(contact.dragFrame.inverse).sub(contact.dragFrame.origin);
    if(contact.dragFrame.pouchTangent)delta.applyMatrix3(contact.dragFrame.pouchTangent);
    contact.dragTarget=contact.grabPoint.map((n,a)=>n+delta.getComponent(a)+contact.dragBias[a]);this.dirty=true;
  }
  move(e){
    if(this.state?.screen==='pile'){this.pileView?.move(e,this.canvas);return;}
    if(this.secondary?.id===e.pointerId){this.moveGrip(e);return;}
    if(!this.active){
      if(performance.now()-(this.lastHover??0)<33)return;this.lastHover=performance.now();
      this.setRay(e);if(this.canTouch())this.behavior?.nudge?.(this.ray.ray);
      const objects=this.canTouch()?this.pickableMeshes():this.state?.screen==='gacha'?[this.state.phase==='idle'?this.machine.knob:this.pack]:[];
      const over=this.ray.intersectObjects(objects,true).length>0;
      this.canvas.style.cursor=over?(this.canTouch()?'grab':'pointer'):'default';return;
    }
    if(e.pointerId!==this.active.id)return;
    this.dirty=true;
    if(this.active.orbit){
      this.toy.rotation.y+=(e.clientX-this.active.x)*.008;
      this.toy.rotation.x=clamp(this.toy.rotation.x+(e.clientY-this.active.y)*.004,-.5,.5);
      this.active.x=e.clientX;this.active.y=e.clientY;return;
    }
    const v=this.viewport,pad=24;
    this.setRay({clientX:clamp(e.clientX,Math.min(v.left+pad,this.active.x),Math.max(v.left+v.width-pad,this.active.x)),clientY:clamp(e.clientY,Math.min(v.top+pad,this.active.y),Math.max(v.top+v.height-pad,this.active.y))});
    this.behavior?.nudge?.(this.ray.ray);
    this.ray.setFromCamera(this.pointer,this.dragFrame.camera);
    if(this.active.magnet&&!this.dragFrame.magnetAttached){
      if(this.magnet.anchor)this.dragFrame.magnetAttached=true;
      else if(this.dragFrame.magnetSurface){
        const surfaceHit=this.ray.intersectObject(this.dragFrame.magnetSurface,false)[0];
        if(surfaceHit){
          // A screen-plane drag cannot approach a curved object in depth by
          // itself. Meet the rest skin once, then keep that plane for the pull.
          const normal=this.dragFrame.camera.getWorldDirection(new T.Vector3());
          const scale=Math.cbrt(1/Math.abs(this.dragFrame.inverse.determinant()));
          const near=surfaceHit.point.clone().addScaledVector(normal,-this.magnet.extent*.07*scale);
          this.plane.setFromNormalAndCoplanarPoint(normal,near);
          if(this.ray.ray.intersectPlane(this.plane,this.hit)){
            this.dragFrame.origin.copy(this.hit).applyMatrix4(this.dragFrame.inverse);
            this.grabPoint=this.dragFrame.origin.toArray();
          }
        }
      }
    }
    if(!this.ray.ray.intersectPlane(this.plane,this.hit))return;
    const displacement=this.hit.clone().applyMatrix4(this.dragFrame.inverse).sub(this.dragFrame.origin);
    if(this.dragFrame.pouchTangent)displacement.applyMatrix3(this.dragFrame.pouchTangent);
    this.dragTarget=this.grabPoint.map((n,a)=>n+displacement.getComponent(a)+(this.dragBias?.[a]??0));
    if(this.active.magnet){this.magnet.move(this.dragTarget);return;}
    if(this.active.tool){this.behavior?.moveTool?.(this.dragTarget);return;}
    if(this.tearPair){
      // Keep separate pieces on the same depth plane so a screen-space drag
      // can bring them together without an invisible gap behind the surface.
      const delta=displacement.toArray();delta[2]=0;
      this.tearPair.move(delta);
    }
    this.cherries?.move(this.dragTarget.map((n,a)=>n-this.grabPoint[a]));
    if(this.stickyPieces){const delta=displacement.toArray();delta[2]=0;this.stickyPieces.move(delta);}

  }
  up(e){
    if(this.state?.screen==='pile'){this.pileView?.up(e);this.canvas.style.cursor='grab';return;}
    if(this.secondary&&(this.active?.id===e.pointerId||this.secondary.id===e.pointerId)){
      if(e.type!=='pointerup'){this.release('grip-cancelled');return;}
      if((!this.active.sampled||!this.secondary.sampled)&&this.canTouch()){
        const now=performance.now(),at=this.active.sampled?this.secondary.at:Math.min(this.active.at,this.secondary.at);
        const dt=clamp((now-at)/1000,1/240,1/60);
        this.tickPhysics(dt,now/1000);this.sampledPhysicsTime=(this.sampledPhysicsTime??0)+dt;
      }
      // A move can arrive after the last frame and immediately before up.
      // Refresh constraints from that input without integrating physics again.
      this.moveHeldContacts(Math.max(performance.now()/1000,this.contactTime??0));
      const survivor=this.active.id===e.pointerId?this.secondary:this;
      // Keep the crossing limit in the surviving finger's input offset. Its
      // removal must not suddenly expose the unclamped glass coordinates.
      if(survivor.gripCorrection){
        survivor.dragTarget=survivor.dragTarget.map((n,a)=>n+survivor.gripCorrection[a]);
        survivor.dragBias=survivor.dragBias.map((n,a)=>n+survivor.gripCorrection[a]);
      }
      // Promote the surviving constraint itself, including its compliance and
      // frozen plane. Re-grabbing here would snap the skin back under the hand.
      if(this.active.id===e.pointerId){
        const contact=this.secondary;
        this.active={id:contact.id,x:contact.x,y:contact.y,at:contact.at,pointerType:'touch',sampled:contact.sampled,orbit:false};
        for(const key of ['grabPoint','dragTarget','dragBias','dragFrame','pressDirection','pressStarted'])this[key]=contact[key];
        this.plane.copy(contact.plane);this.body.hold=this.body.supportHold;
      }
      this.body.supportHold=null;this.secondary=null;this.gripCorrection=null;this.supportStart=null;this.dirty=true;return;
    }
    if(this.active?.id!==e.pointerId)return;
    // A quick tap is too short to press the skin in; it gets a little poke instead.
    const tapped=e.type==='pointerup'&&this.isTap();
    if(e.type==='pointerup'&&!this.active.orbit){this.behavior?.pointerUp?.();toySound.release();}
    // A quick tap may finish between two animation frames. Sample a completed
    // contact once; cancellations must never create an extra squeeze.
    if(e.type==='pointerup'&&!this.active.orbit&&!this.active.sampled&&this.canTouch()){
      const now=performance.now();
      const dt=clamp((now-this.active.at)/1000,1/240,1/60);
      this.tickPhysics(dt,now/1000);
      this.sampledPhysicsTime=(this.sampledPhysicsTime??0)+dt;
    }
    this.release();
    if(tapped)this.pokeSkin();
  }
  isTap(){
    const a=this.active,b=this.body;
    if(!a||a.orbit||a.tool||a.magnet||!b?.hold||this.secondary||this.crackle&&this.crackle.damage.length<this.shells.length)return false;
    const size=Math.max(...b.max.map((n,i)=>n-b.min[i]));
    return performance.now()-(this.pressStarted??0)<260&&Math.hypot(...this.dragTarget.map((n,i)=>n-this.grabPoint[i]))<size*.08;
  }
  // The skin under the tap dents in along the press and wobbles back.
  pokeSkin(){
    const b=this.body,g=this.grabPoint,d=this.pressDirection,r=b.initialRest,size=Math.max(...b.max.map((n,a)=>n-b.min[a]));
    for(let i=0;i<b.count;i++){
      const dx=r[i*3]-g[0],dy=r[i*3+1]-g[1],dz=r[i*3+2]-g[2],w=Math.exp(-(dx*dx+dy*dy+dz*dz)/(size*size*.04));
      for(let a=0;a<3;a++)b.velocity[i*3+a]+=d[a]*size*3*w;
    }
    b.activity=1;this.dirty=true;
  }
  supportPress(){
    this.supportStart=null;
    if(!['pouch','foam'].includes(catalogItem(this.kind)?.material))return;
    const b=this.body,d=this.pressDirection,a=Math.abs(d[0])>Math.abs(d[1])?(Math.abs(d[0])>Math.abs(d[2])?0:2):(Math.abs(d[1])>Math.abs(d[2])?1:2);
    const point=this.grabPoint.map((v,i)=>clamp(v,b.min[i]+(b.max[i]-b.min[i])*.08,b.max[i]-(b.max[i]-b.min[i])*.08));
    point[a]=d[a]<0?b.min[a]+(b.max[a]-b.min[a])*.06:b.max[a]-(b.max[a]-b.min[a])*.06;
    this.supportStart=point;b.support(point);
  }
  moveHeldContacts(now){
    this.contactTime=now;
    this.pressTime=this.autoPressUntil?this.autoPressElapsed:Math.max(0,now-this.pressStarted/1000);
    const depth=this.body.max[2]-this.body.min[2];
    const remaining=this.crackle?1-this.crackle.damage.length/this.shells.length:0;
    const rigidShell=remaining>.3;
    const pressDepth=(this.crackle?.recoil??0)*.05+(rigidShell?.10:this.pouch?Math.min(.58,depth*.55):this.kind==='pancake'?Math.min(.45,depth*.36):Math.min(.34,depth*.28));
    const press=pressDepth*(1-Math.exp(-this.pressTime*7));
    const pullScale=this.behavior?.pullScale??(this.tearPair?.08:rigidShell?.12:this.cherries?.held!==null&&this.cherries?.held!==undefined?.15:1);
    const target=this.dragTarget.map((n,a)=>this.grabPoint[a]+(n-this.grabPoint[a])*pullScale+this.pressDirection[a]*press);
    // Pulled down toward the floor, the skin below the finger squeezes to half
    // its height at most, so a face is never crushed flat against the floor.
    const floorY=this.body.floor,lowest=contact=>floorY==null?-Infinity:floorY+(contact.grabPoint[1]-floorY)*.5;
    target[1]=Math.max(lowest(this),target[1]);
    if(this.secondary){
      const contact=this.secondary;
      const secondPress=pressDepth*(1-Math.exp(-Math.max(0,now-contact.pressStarted/1000)*7));
      const second=contact.dragTarget.map((n,a)=>contact.grabPoint[a]+(n-contact.grabPoint[a])*pullScale+contact.pressDirection[a]*secondPress);
      second[1]=Math.max(lowest(contact),second[1]);
      // Do not let fingers crossing on glass reverse the two skin contacts.
      const axis=contact.grabPoint.map((n,a)=>n-this.grabPoint[a]),length=Math.hypot(...axis);
      const separation=axis.reduce((sum,n,a)=>sum+n/length*(second[a]-target[a]),0);
      const correction=Math.max(0,length*.18-separation)*.5;
      this.gripCorrection=axis.map(n=>-n/length*correction/pullScale);contact.gripCorrection=this.gripCorrection.map(n=>-n);
      for(let a=0;a<3;a++){target[a]-=axis[a]/length*correction;second[a]+=axis[a]/length*correction;}
      this.body.moveSupport(second);
    }
    this.body.move(target);return press;
  }
  release(reason='interaction'){if(this.carryOn){this.carry.release();this.carryOn=false;}this.pileView?.release();this.canvas.dataset.releaseReason=reason;this.dirty=true;this.active=null;this.secondary=null;this.dragFrame=null;if(!this.extras?.peel())this.body?.release();this.cherries?.release();this.tearPair?.release();this.stickyPieces?.release();this.meshExtrusion?.release();this.autoPressUntil=0;this.autoTearUntil=0;this.autoMagnetUntil=0;this.magnet?.setActive(false);this.autoSticky=false;this.behavior?.release?.(reason);this.pressAmount=0;this.canvas.style.cursor='default';this.flushPendingResize?.();}
  squeeze(){
    if(!this.body||!this.canTouch())return;
    if(this.active)this.release('squeeze-demo');
    if(this.behavior?.squeeze?.()){this.dirty=true;return;}
    if(this.magnet){this.release('magnet-demo');this.magnetDemoPath={...createMagnetDemoPath(this.body,this.magnet.surfacePoints),start:[...this.magnet.position]};this.magnet.setActive(true);this.autoMagnetUntil=1;this.autoMagnetTime=0;this.dirty=true;return;}
    if(this.tearPair){
      if(!this.tearPair.attached)this.tearPair.reset();
      this.tearPair.grab(1);
      this.autoTearUntil=1;this.autoTearElapsed=0;this.dirty=true;return;
    }
    const b=this.body,pizza=this.kind==='cheesepizza',height=b.max[1]-b.min[1],front=b.min[2]+(b.max[2]-b.min[2])*.9;
    const topPress=this.kind==='pancake'||this.kind==='macaron',point=pizza?[(b.min[0]+b.max[0])*.5,b.min[1]+height*.22,front]:this.kind==='pancake'?[0,b.min[1]+height*.80,(b.min[2]+b.max[2])*.5]:topPress?[0,b.max[1]*.76,b.max[2]*.35]:[0,b.max[1]*.62,b.max[2]*.95];
    if(this.stickyPieces){this.stickyPieces.grab(0);this.autoSticky=true;}
    const custom=this.behavior?.squeezePoint?.(b),pressPoint=custom?.point??point;
    b.grab(pressPoint);this.grabPoint=pressPoint;this.dragTarget=pressPoint;this.pressDirection=custom?.direction??(pizza?[0,0,0]:this.kind==='pancake'?[0,-1,0]:topPress?[0,-1,-.15]:[0,-.5,-1]);
    this.supportPress();
    if(pizza){this.supportStart=[point[0],b.min[1]+height*.84,front];b.support(this.supportStart);}
    this.pressStarted=performance.now();this.autoPressUntil=this.pressStarted+650;this.autoPressElapsed=0;this.pressTime=0;
  }
  flip(){if(this.canTouch()&&this.behavior?.flip?.())this.dirty=true;}
  reset(){
    this.shadowDirty=true;
    this.release();this.body?.reset();this.beads?.reset();this.cherries?.reset();this.pouch?.reset();this.meshExtrusion?.reset();this.tearPair?.reset();this.stickyPieces?.reset();this.aurora?.reset();this.magnet?.reset();this.behavior?.reset?.();if(this.magnetTool)this.magnetTool.position.fromArray(this.magnet.position);
    const record=this.state.previewKind?{waxBroken:this.state.previewWaxBroken,waxDamage:this.state.previewWaxDamage}:this.state.collection.find(t=>t.id===this.state.selectedId);
    this.crackle=this.shells.length?new CrackleShell(this.shells.map(s=>s.center.toArray()),record?.waxBroken?this.shells.map((_,i)=>i):record?.waxDamage??[],crackProfile(this.kind)):null;this.needsSurfaceUpdate=true;this.toy.rotation.set(...(this.behavior?.rotation??[0,-.18,0]));
    this.melt=0;this.extras?.reset();this.carry?.reset();
    for(const s of this.meshes.filter(item=>item.isShell)){s.isCrumbled=false;s.mesh.visible=true;s.mesh.position.set(0,0,0);s.mesh.rotation.set(0,0,0);s.mesh.scale.setScalar(1);}
  }
  // Kneading sounds follow the finger's speed (in toy sizes per second), and
  // counters that behaviours publish turn into the toy's event sounds.
  soundStep(dt){
    if(!toySound.on){toySound.watch(this.canvas.dataset);this.soundDrag=null;return;}
    const target=this.active&&!this.active.orbit&&this.body?.hold?this.dragTarget:null;
    if(target&&this.soundDrag&&dt>0){
      const size=Math.max(...this.body.max.map((n,a)=>n-this.body.min[a]))||1;
      toySound.knead(Math.hypot(...target.map((n,a)=>n-this.soundDrag[a]))/dt/size);
    }
    this.soundDrag=target?[...target]:null;
    toySound.watch(this.canvas.dataset);
  }
  advancePhysics(elapsed,now){
    if(!Number.isFinite(elapsed)||elapsed<=0)return;
    // Keep 15–120 Hz displays on the same clock. Bound background catch-up,
    // and deform the render mesh only after the final small physics step.
    // Pointer-up can already have sampled part of this frame. Consume that
    // time once, carrying any remainder into the following frame.
    const sampled=Math.min(elapsed,this.sampledPhysicsTime??0);
    this.sampledPhysicsTime=Math.max(0,(this.sampledPhysicsTime??0)-sampled);
    let duration=Math.min(elapsed-sampled,.1);
    // Hitstop: a brief freeze on a hard impact, a standard fighting-game cue.
    if(this.hitstop>0){const frozen=Math.min(this.hitstop,duration);this.hitstop-=frozen;duration-=frozen;}
    if(duration<=0)return;
    const steps=Math.ceil(duration/(1/30)),dt=duration/steps;
    for(let i=0;i<steps;i++)this.tickPhysics(dt,now-duration+(i+1)*dt,i===steps-1);
  }
  tickPhysics(dt,now,updateSurface=true){
    if(!this.body||!this.canTouch())return;
    if(this.active&&!this.active.orbit&&!this.active.magnet&&!this.active.tool||this.autoPressUntil){
      if(this.autoPressUntil){this.autoPressElapsed+=dt;this.canvas.dataset.demoTime=this.autoPressElapsed.toFixed(3);}
      if(this.active&&!this.active.orbit)this.active.sampled=true;
      if(this.secondary)this.secondary.sampled=true;
      const pizzaDemo=this.autoPressUntil&&this.kind==='cheesepizza';
      if(pizzaDemo){
        // Keep the crust in place while the cheese tip slowly yields below it.
        const t=Math.min(1,this.autoPressElapsed/.65),pull=t*t*(3-2*t),height=this.body.max[1]-this.body.min[1];
        this.dragTarget=[this.grabPoint[0]+height*.09*pull,this.grabPoint[1]-height*.25*pull,this.grabPoint[2]];
      }
      if(this.carryOn&&this.active&&!this.secondary)this.followCarry(dt);
      const press=this.moveHeldContacts(now);
      this.pressAmount=1-Math.exp(-this.pressTime*7);
      if(this.stickyPieces){
        this.stickyPieces.press(Math.min(1,press/.34),this.pressDirection);
        if(this.autoSticky)this.stickyPieces.move([-.9*Math.min(1,this.autoPressElapsed/.65),.1,0]);
      }
      if(this.supportStart)this.body.moveSupport(pizzaDemo?this.supportStart:this.supportStart.map((n,a)=>n+(this.dragTarget[a]-this.grabPoint[a])*.7));

    }
    if(this.magnet){
      if(this.active?.magnet)this.active.sampled=true;
      if(this.autoMagnetUntil){
        this.autoMagnetTime+=dt;this.magnet.move(magnetDemoTarget(this.body,this.magnet.home,this.autoMagnetTime,this.magnetDemoPath));
        if(this.autoMagnetTime>=2.7){this.autoMagnetUntil=0;this.magnet.setActive(false);}
      }
      this.magnet.step(dt);this.magnetTool.position.fromArray(this.magnet.position);
    }
    if(this.meshExtrusion){
      this.meshExtrusion.move(this.body.hold?Math.min(1,Math.hypot(...this.body.hold.target.map((n,a)=>n-this.body.hold.start[a]))*4):0);
      this.meshExtrusion.step(dt);this.canvas.dataset.extrusion=this.meshExtrusion.pressure.toFixed(3);
    }
    const stickyMoving=this.stickyPieces&&(this.stickyPieces.activity>.0001||this.stickyPieces.held!==null);
    this.stickyPieces?.step(dt);
    if(this.stickyPieces){this.body.activity=0;this.canvas.dataset.pieceBonds=String(this.stickyPieces.bonds.filter(b=>b.attached).length);this.canvas.dataset.pieceOffsets=JSON.stringify(this.stickyPieces.positions.map(p=>p.map(n=>+n.toFixed(3))));}
    if(this.active?.tool)this.active.sampled=true;
    this.behavior?.step(dt);
    if(this.carry?.busy){const impact=this.carry.step(dt);if(impact>.6)this.landCarry(impact);this.dirty=true;}
    this.soundStep(dt);
    // A behaviour that snaps to rest in this step still needs that final pose drawn.
    const behaviorMoving=this.behavior?.activity>0;if(this.behaviorMoving&&!behaviorMoving)this.needsSurfaceUpdate=true;this.behaviorMoving=behaviorMoving;
    const moving=this.behavior?.activity>0||this.body.activity>.0002||this.body.hold||this.carry?.busy||this.magnet?.active||this.beads?.activity>.002||this.cherries?.activity>.001||this.pouch?.activity>.0003||this.meshExtrusion?.activity>.0001||this.tearPair?.activity>.0001||stickyMoving||this.stickyPieces?.activity>.0001;
    if(this.tearPair){
      if(this.autoTearUntil){
        this.autoTearElapsed+=dt;
        const pullTime=.95*Math.sqrt(this.tearPair.stretch),progress=Math.min(1,this.autoTearElapsed/pullTime);
        this.tearPair.move([this.tearPair.tearDistance*1.15*progress,0,0]);
      }
      this.tearPair.step(dt);this.canvas.dataset.tearPhase=this.tearPair.phase;
      this.canvas.dataset.tearTime=(this.autoTearElapsed??0).toFixed(3);this.canvas.dataset.tearExtension=(this.tearPair.extension/this.tearPair.scale).toFixed(3);this.canvas.dataset.tornStrands=String(this.tearPair.strands.filter(s=>!s.intact).length);
      if(this.autoTearUntil&&this.autoTearElapsed>=.95*Math.sqrt(this.tearPair.stretch)+.35){this.tearPair.release();this.autoTearUntil=0;}
    }
    const contact=this.body.hold&&this.dragTarget?this.dragTarget:null;
    const chips=this.crackle?.step(dt,contact)??[];
    if(chips.length){this.events.crack(chips);this.canvas.dataset.crackedPieces=String(this.crackle.damage.length);}
    // A melting core slumps toward how much of its shell is gone, slowly, like thick slime.
    const meltTarget=this.meshes.some(item=>item.melts)&&this.crackle?this.crackle.damage.length/this.shells.length:0;
    const previousMelt=this.melt??0;this.melt=previousMelt+(meltTarget-previousMelt)*(1-Math.exp(-dt/1.6));
    if(Math.abs(meltTarget-this.melt)<.002)this.melt=meltTarget;
    this.melting=this.melt!==previousMelt;
    this.extras?.step(dt);
    const breaking=this.crackle?.animating||this.melting||this.extras?.active;
    const colorChanged=this.aurora?.step(dt,this.body.hold);
    if(!moving&&!breaking&&!this.needsSurfaceUpdate){
      if(colorChanged){if(updateSurface)this.aurora.update();else this.needsSurfaceUpdate=true;}
      return;
    }
    this.needsSurfaceUpdate=false;
    if(moving&&!this.stickyPieces)this.body.step(dt);
    this.behavior?.afterBodyStep?.(dt);
    if(this.tearPair)this.tearPair.setSurfaceAnchors(this.tearAnchorBindings.map(b=>this.body.sample(b)));
    this.pouch?.step(dt);
    if(this.cherries){
      this.cherries.step(dt);
      this.canvas.dataset.attached=String(this.cherries.attached);
      const gap=Math.hypot(...this.cherries.positions[1].map((n,a)=>n-this.cherries.positions[0][a]));
      this.canvas.dataset.maxSeparation=Math.max(gap,Number(this.canvas.dataset.maxSeparation??0)).toFixed(3);
    }
    if(this.beads){
      this.beads.step(dt,this.particles.map(item=>this.body.sample(item.centerBinding)));
    }
    if(this.autoPressUntil&&this.autoPressElapsed>=.65){this.body.release();this.stickyPieces?.release();this.autoSticky=false;this.autoPressUntil=0;}
    if(!updateSurface){this.needsSurfaceUpdate=true;return;}
    this.aurora?.update();
    const out=[0,0,0],normal=[0,0,0],extruded=[0,0,0],instanceMatrix=new T.Matrix4();
    const bodySurface=this.stickyPieces?null:prepareSurface(this.pouch?.filling??this.body),pouchSurface=this.pouch?prepareSurface(this.pouch):null;
    const melt=this.melt??0,meltCenter=melt>0&&(this.shellOrigin??this.shells.reduce((sum,g)=>sum.add(g.center),new T.Vector3()).divideScalar(Math.max(1,this.shells.length)));
    // A rigid shell (hard candy) does not flex with the core: its whole pieces
    // share one offset, so no seam opens until a crack reaches it.
    let shellShift=null;
    if(this.behavior?.rigidShell&&this.crackle){
      shellShift=[0,0,0];let whole=0;
      this.shells.forEach((group,i)=>{if(this.crackle.isBroken(i))return;const p=this.body.sample(group.binding);shellShift[0]+=p[0]-group.center.x;shellShift[1]+=p[1]-group.center.y;shellShift[2]+=p[2]-group.center.z;whole++;});
      shellShift=shellShift.map(v=>whole?v/whole*.2:0);
    }
    for(const item of this.meshes){
      if(item.isProp){this.behavior?.poseProp?.(item);continue;}
      if(item.isShell&&this.crackle?.isBroken(item.shellIndex)){
        if(!item.isCrumbled){
          item.mesh.geometry.attributes.position.array.set(item.rest);item.mesh.geometry.attributes.position.needsUpdate=true;
          item.mesh.geometry.attributes.normal.array.set(item.restNormals);item.mesh.geometry.attributes.normal.needsUpdate=true;
          updateSurfaceBounds(item.mesh.geometry);item.isCrumbled=true;
        }
        const group=this.shells[item.shellIndex],rest=group.center;
        this.shellOrigin??=this.shells.reduce((sum,g)=>sum.add(g.center),new T.Vector3()).divideScalar(this.shells.length);
        // A toy may fly its own debris; otherwise shards pop and settle near the core.
        const pose=this.behavior?.shellPose?.(item.shellIndex,this.crackle.ages[item.shellIndex])??waxFragmentPose(item.shellIndex,this.crackle.ages[item.shellIndex],rest.toArray(),this.body.sample(group.binding),0,this.shellOrigin.toArray(),crackProfile(this.kind));
        item.mesh.rotation.fromArray(pose.rotation);item.mesh.scale.setScalar(pose.scale);
        const rotated=rest.clone().multiplyScalar(pose.scale).applyEuler(item.mesh.rotation);
        item.mesh.position.fromArray(pose.center).sub(rotated);
        item.mesh.visible=true;continue;
      }
      if(item.isShell){
        const group=this.shells[item.shellIndex];
        if(shellShift)item.mesh.position.fromArray(shellShift);
        else{const position=this.body.sample(group.binding);item.mesh.position.set((position[0]-group.center.x)*.2,(position[1]-group.center.y)*.2,(position[2]-group.center.z)*.2);}
        // Cracked but still attached: a toy may sink and tilt the piece about its centre.
        const nudge=this.behavior?.shellNudge?.(item.shellIndex);
        if(nudge){item.mesh.rotation.fromArray(nudge.rotation);const turned=group.center.clone().applyEuler(item.mesh.rotation);item.mesh.position.add(group.center).sub(turned).add(tmpContact.fromArray(nudge.offset));}
        else if(item.mesh.rotation.x||item.mesh.rotation.y||item.mesh.rotation.z)item.mesh.rotation.set(0,0,0);
        // On a rigid shell only cracked pieces rattle.
        if(this.crackle.charge>.05&&this.dragTarget&&(!shellShift||nudge)){
          const shudder=waxTremble(item.shellIndex,now,this.crackle.charge,group.center.distanceTo(tmpContact.fromArray(this.dragTarget)));
          item.mesh.position.x+=shudder[0];item.mesh.position.y+=shudder[1];item.mesh.position.z+=shudder[2];
        }
        continue;
      }
      const attr=item.mesh.geometry.attributes.position;
      if(this.stickyPieces){
        const normals=item.mesh.geometry.attributes.normal;
        for(let i=0;i<attr.count;i++){
          const k=i*3,index=item.stickyIndices[i];
          this.stickyPieces.sample(index,item.rest[k],item.rest[k+1],item.rest[k+2],out);
          this.stickyPieces.normal(index,item.restNormals[k],item.restNormals[k+1],item.restNormals[k+2],normal,item.rest[k],item.rest[k+1],item.rest[k+2]);
          attr.setXYZ(i,...out);normals.setXYZ(i,...normal);
        }
        normals.needsUpdate=true;
      }else if(item.rigidParticle){
        const position=this.beads.positions[item.particleIndex];
        if(item.instances){
          const r=this.beads.radii[item.particleIndex];
          item.instances.setMatrixAt(item.instanceIndex,instanceMatrix.makeScale(r,r,r).setPosition(...position));
          item.instances.instanceMatrix.needsUpdate=true;continue;
        }
        const offset=position.map((n,a)=>n-item.center.getComponent(a));
        for(let i=0;i<attr.count;i++)attr.setXYZ(i,item.rest[i*3]+offset[0],item.rest[i*3+1]+offset[1],item.rest[i*3+2]+offset[2]);
      }else for(let i=0;i<item.bindings.length;i++){
        if(item.behaviorSample)this.behavior.sample(item,i,out);
        else if(item.transportNormals){
          sampleSurface(item.isFilm&&pouchSurface?pouchSurface:bodySurface,item.bindings[i],out,normal,item.bindings[i].retention??(item.isShell?.2:1));
          item.mesh.geometry.attributes.normal.setXYZ(i,normal[0],normal[1],normal[2]);
        }else this.body.sample(item.bindings[i],out);
        if(this.tearPair){
          const side=item.mesh.name.startsWith('body_left')?-1:1;
          this.tearPair.sampleLobe(side,out,out,item.transportNormals?normal:null);
          if(item.transportNormals)item.mesh.geometry.attributes.normal.setXYZ(i,normal[0],normal[1],normal[2]);
        }
        if(this.cherries)this.cherries.deform(out,item.cherrySide,item.cherryJointWeights?.[i]??0);
        if(item.nearestParticles&&this.body.hold){
          const j=item.nearestParticles[i],p=this.beads.positions[j],r=this.beads.radii[j];
          const dx=out[0]-p[0],dy=out[1]-p[1],dz=out[2]-p[2],distance=Math.hypot(dx,dy,dz);
          const pressure=Math.min(1,Math.hypot(...this.body.hold.target.map((n,a)=>n-this.body.hold.start[a]))*3);
          const bump=.065*pressure*Math.exp(-((distance-r)**2)/(r*r*1.7));
          if(distance>.001){out[0]+=dx/distance*bump;out[1]+=dy/distance*bump;out[2]+=dz/distance*bump;}
        }
        if(this.meshExtrusion){
          const k=i*3;
          if(item.extrusionBindings){
            this.meshExtrusion.sample(item.extrusionBindings[i],extruded,.5);
            for(let a=0;a<3;a++)out[a]=extruded[a]+(out[a]-item.rest[k+a])*.25;
          }else for(let a=0;a<3;a++)out[a]=item.rest[k+a]+(out[a]-item.rest[k+a])*.25;
        }
        if(item.melts&&melt>0){
          // The soft core slumps under its own weight: the top sinks and the base
          // spreads, most at the floor, like thick slime losing its shape.
          const height=Math.max(.001,meltCenter.y*2),t=Math.min(1,Math.max(0,out[1]/height));
          const wide=1+melt*(.12+.55*(1-t)**2);
          out[0]=meltCenter.x+(out[0]-meltCenter.x)*wide;out[2]=meltCenter.z+(out[2]-meltCenter.z)*wide;
          out[1]*=1-.62*melt*(.35+.65*t);
        }
        if(item.behaviorDeform){
          this.behavior.deform(item,i,out,item.transportNormals?normal:null);
          if(item.transportNormals)item.mesh.geometry.attributes.normal.setXYZ(i,normal[0],normal[1],normal[2]);
        }
        attr.setXYZ(i,out[0],out[1],out[2]);
      }
      attr.needsUpdate=true;
      if(item.transportNormals)item.mesh.geometry.attributes.normal.needsUpdate=true;
      else if(!item.rigidParticle)item.mesh.geometry.computeVertexNormals();
      updateSurfaceBounds(item.mesh.geometry);
    }
    for(const group of this.beadRenderGroups??[]){group.computeBoundingSphere();group.computeBoundingBox();}
    if(this.meshBulbs){
      this.meshExtrusion.getBulbs().forEach((pose,index)=>{
        const binding=this.meshBulbBindings[index],sample=this.body.sample(binding);
        const center=pose.position.map((n,a)=>n+(sample[a]-binding.point[a])*.25);
        this.meshBulbs.setMatrixAt(index,instanceMatrix.makeScale(pose.radius,pose.radius,pose.radius).setPosition(...center));
      });
      this.meshBulbs.instanceMatrix.needsUpdate=true;this.meshBulbs.computeBoundingSphere();this.meshBulbs.computeBoundingBox();
    }
    if(this.tearBridge){
      this.tearBridge.visible=true;
      if(this.tearBridge.visible)updateTearBridge(this.tearBridge.geometry,this.tearPair,this.body,this.tearAnchorBindings);
    }
    if(this.stickyBridges)this.stickyBridges.visible=updateStickyBridges(this.stickyBridges.geometry,this.stickyPieces.bridges());
  }
  loop(){
    requestAnimationFrame(()=>this.loop());if(document.hidden||this.contextLost)return;
    const now=performance.now(),elapsed=(now-this.previous)/1000,dt=Math.min(elapsed,1/30);this.previous=now;
    if(!this.state)return;
    if(this.state.screen==='pile'){
      const physicsStart=performance.now(),changed=this.pileView?.update(dt);
      this.canvas.dataset.physicsMs=(performance.now()-physicsStart).toFixed(1);
      if(changed||this.dirty){
        const renderStart=performance.now();this.dirty=false;this.renderer.render(this.pileView.scene,this.pileView.camera);
        this.canvas.dataset.renderMs=(performance.now()-renderStart).toFixed(1);this.canvas.dataset.renderCount=String(++this.frame);
        this.canvas.dataset.pileCount=String(this.pileView.models.length);this.canvas.dataset.drawCalls=String(this.renderer.info.render.calls);this.canvas.dataset.rendering='active';
      }else this.canvas.dataset.rendering='idle';
      return;
    }
    const time=now/1000,age=time-this.phaseStart,s=this.state,reduced=s.reducedMotion;
    const animated=s.screen==='gacha'&&(s.phase==='rolling'||s.phase==='revealed'&&!this.revealSettled)||this.canTouch()&&(this.active||this.autoPressUntil||this.body?.activity>.0002||this.beads?.activity>.002||this.cherries?.activity>.001||this.pouch?.activity>.0003||this.meshExtrusion?.activity>.0001||this.tearPair?.activity>.0001||this.autoTearUntil||this.crackle?.animating||this.melting||this.extras?.active||this.aurora?.active||this.magnet?.active||this.autoMagnetUntil||this.behavior?.active);
    this.stepFace(dt,reduced);
    const shadowDue=this.shadowPending&&now-(this.lastShadow??0)>=100;
    if(!this.dirty&&!animated&&!this.playFitSettling&&!this.shadowDirty&&!shadowDue&&!(this.canTouch()&&this.stickyPieces?.activity>.0001)){this.canvas.dataset.rendering='idle';return;}
    this.canvas.dataset.rendering='active';
    if(now-(this.lastRenderAt??0)>200)this.frameTimes=[];
    else this.frameTimes.push((now-this.lastRenderAt)/1000);
    if(this.frameTimes.length>60)this.frameTimes.shift();this.lastRenderAt=now;
    if(this.shadowDirty||this.lastShadow===undefined||now-this.lastShadow>=100){
      this.renderer.shadowMap.needsUpdate=true;this.lastShadow=now;this.shadowDirty=false;this.shadowPending=false;
    }else this.shadowPending=true;
    this.dirty=false;
    const inGacha=s.screen==='gacha',showToy=!inGacha||s.phase==='revealed';
    this.platform.visible=false;this.platformTrim.visible=false;
    this.hemi.intensity=inGacha?1.2:.55;this.key.intensity=inGacha?2.6:2.2;
    this.fill.intensity=inGacha?1.2:.65;this.scene.environmentIntensity=inGacha?.55:.8;
    this.floor.position.y=-.05;this.floor.material.opacity=inGacha?.13:.09;
    const opening=revealPose(age,reduced);
    const openProgress=inGacha&&s.phase==='revealed'?opening.progress:0;
    this.machine.group.visible=inGacha&&(s.phase!=='revealed'||openProgress<1);
    this.toy.visible=showToy&&!this.loading&&!!this.recordId&&!['collection','catalog'].includes(s.screen);
    this.pack.visible=inGacha&&['rolling','sealed','revealed'].includes(s.phase);
    this.machine.group.rotation.y=0;
    this.machine.group.position.set(-4*openProgress,0,-2*openProgress);
    const pose=dispensePose(s.phase==='rolling'?(reduced?age*4:age):s.phase==='sealed'?4:0);
    this.machine.group.rotation.z=inGacha&&s.phase==='rolling'&&!reduced?pose.recoil:0;
    this.machine.knob.rotation.z=pose.knob;this.machine.flap.rotation.x=pose.flap;
    for(let i=0;i<this.machine.balls.length;i++){
      const {ball,rest}=this.machine.balls[i];ball.position.copy(rest);
      ball.visible=!(i===0&&inGacha&&((s.phase==='rolling'&&pose.visible)||s.phase==='sealed'));
      if(s.phase==='rolling'&&!reduced){
        ball.position.copy(capsuleDisplayPose(rest,age,pose.churn));
        ball.rotation.z+=dt*5*pose.churn;
      }
    }
    let close=0;
    if(inGacha&&this.pack.userData.top)for(const mesh of [this.pack.userData.top,this.pack.userData.bottom]){mesh.position.set(0,0,0);mesh.rotation.set(0,0,0);}
    for(const mesh of this.pack.children)mesh.material.opacity=1;
    if(inGacha&&s.phase==='rolling'){
      this.pack.scale.setScalar(1);this.pack.position.fromArray(pose.ball);this.pack.visible=pose.visible;
      this.pack.rotation.set(pose.roll,0,-pose.roll*.65);
      this.pack.userData.top.position.y=0;this.pack.userData.bottom.position.y=0;this.pack.userData.ring.visible=true;
      close=ease((age*(reduced?4:1)-2.15)/1.2);
      if(pose.finished&&!this.sealSent){this.sealSent=true;this.events.seal();}
    }else if(inGacha&&s.phase==='sealed'){
      close=1;this.pack.scale.setScalar(1);this.pack.position.fromArray(dispensePose(4).ball);this.pack.rotation.set(4.77,0,-3.10);
      this.pack.userData.top.position.y=0;this.pack.userData.bottom.position.y=0;this.pack.userData.ring.visible=true;
    }else if(inGacha&&s.phase==='revealed'){
      if(!this.revealSettled)this.toy.rotation.z=opening.toyTilt;
      this.revealSettled=opening.finished;
      const t=openProgress;close=1-t;
      this.pack.scale.setScalar(opening.packScale);this.pack.position.copy(new T.Vector3(...dispensePose(4).ball).lerp(new T.Vector3(0,1.1,0),t));
      const unwind=1-ease(age/.28);this.pack.rotation.set(4.77*unwind,0,-3.10*unwind);
      this.pack.userData.top.position.fromArray(opening.top);this.pack.userData.bottom.position.fromArray(opening.bottom);
      this.pack.userData.top.rotation.z=opening.topTilt+opening.tension;this.pack.userData.bottom.rotation.z=opening.bottomTilt-opening.tension;
      this.pack.userData.ring.visible=age<.285&&!reduced;
      for(const mesh of this.pack.children)mesh.material.opacity=1-ease((age-.82)/.41);
      this.pack.visible=opening.packVisible;this.toy.visible=this.toy.visible&&opening.toyVisible;this.toy.scale.setScalar(opening.toyScale);
    }else this.toy.scale.setScalar(1);
    const cameraClose=reduced?0:close;
    this.camera.position.copy(this.homePosition).lerp(new T.Vector3(.7,1.7,this.mobile?4.8:4.5),cameraClose);
    const cameraTarget=this.homeTarget.clone().lerp(new T.Vector3(.2,.8,.9),cameraClose);
    if(inGacha&&s.phase==='revealed'&&!reduced){
      this.camera.position.sub(cameraTarget).multiplyScalar(1-opening.focus*.045).add(cameraTarget);
      this.key.intensity+=opening.focus*.7;
    }
    this.camera.lookAt(cameraTarget);
    this.gachaAccent?.update(inGacha?s.phase:'idle',s.phase==='rolling'&&reduced?age*4:age,reduced,this.camera);
    const carry=s.screen==='play'&&this.carry?this.carry.p:null,carried=carry?[...carry]:[0,0,0];
    this.toy.position.set(carried[0],.07+carried[1],carried[2]);
    if(inGacha&&s.phase==='revealed'){
      this.toy.position.add(new T.Vector3(...opening.toyOffset));
    }
    const physicsStart=performance.now();this.advancePhysics(elapsed,time);
    this.followFace();
    // Physics may have moved the carried toy this frame.
    if(carry){const p=this.carry.p;this.toy.position.set(p[0],.07+p[1],p[2]);}
    if(s.screen==='play'&&this.toy.visible){
      // A toy thrown at the screen keeps its scale so it can come close.
      if(this.behavior?.freeFit){this.toy.scale.setScalar(this.playFitScale??1);this.toy.updateMatrixWorld(true);}
      else{
      const previousScale=this.playFitScale??1,held=!!this.body?.hold||!!this.active?.magnet||!!this.autoMagnetUntil||!!this.active?.tool||!!this.behavior?.held;
      this.playFitScale=fitPlayObject(this.toy,this.camera,previousScale,held,Math.min(elapsed,.1));
      this.playFitSettling=!held&&this.playFitScale>previousScale+.00001;
      // The edge of the view is the wall of the play space for a moved toy.
      if(carry&&(this.carry.away||this.carry.busy)){const p=this.carry.p,hit=this.carry.wall([this.toy.position.x-p[0],this.toy.position.y-.07-p[1],this.toy.position.z-p[2]]);if(hit&&hit.speed>.8)this.bumpCarry(hit);}
      }
      this.canvas.dataset.fitScale=this.playFitScale.toFixed(4);
      this.playBounds??=new T.Box3();this.playBounds.setFromObject(this.toy);
      this.floor.position.y=Math.min(-.05,this.playBounds.min.y-.06);
    }else this.playFitSettling=false;
    this.canvas.dataset.physicsMs=(performance.now()-physicsStart).toFixed(1);
    if(this.kind==='gelbeads'&&this.toy.visible)sortGelBeads(this.particles,this.toy,this.camera);
    const renderStart=performance.now();
    // Behaviours with their own offscreen passes (screen-space liquid) draw them
    // first. The pending shadow update belongs to the main scene, so keep it.
    if(this.behavior?.beforeRender&&this.toy.visible){
      const shadowUpdate=this.renderer.shadowMap.needsUpdate;this.renderer.shadowMap.needsUpdate=false;
      this.toy.updateMatrixWorld(true);this.behavior.beforeRender(this.renderer,this.camera);
      this.renderer.shadowMap.needsUpdate=shadowUpdate;
    }
    this.renderer.render(this.scene,this.camera);
    this.canvas.dataset.renderCount=String(this.frame+1);
    this.canvas.dataset.renderMs=(performance.now()-renderStart).toFixed(1);this.canvas.dataset.drawCalls=String(this.renderer.info.render.calls);
    this.frame++;if(this.frame%30===0){this.canvas.dataset.frame=String(this.frame);this.canvas.dataset.energy=(this.body?.activity??0).toFixed(3);if(this.frameTimes.length)this.canvas.dataset.fps=(this.frameTimes.length/this.frameTimes.reduce((a,b)=>a+b,0)).toFixed(1);}
  }
}
